import {
  getVariantOptionsLabel,
  stripImageQuery,
} from "@babackapp/return-variables-registry";
import type { Order } from "../types";

type VariantOption = { name: string; value: string };
type Variant = {
  id: string;
  selectedOptions: VariantOption[];
  image?: string | null;
  /**
   * Whether Shopify gives the variant its own photo. Set by the backend before
   * it copies the product photo into `image`; missing from older payloads.
   */
  hasOwnImage?: boolean;
  sku?: string | null;
};
type Product = {
  productId?: string | number | null;
  productTitle?: string;
  image?: string | null;
  variants: Variant[];
};

export type ExchangeVariantDisplay = {
  /** Shopify product carrying the variant (may differ from the purchased one). */
  productId: string | null;
  productTitle: string | null;
  /** Variant image, else the product image. */
  image: string | null;
  /**
   * Whether `image` is the variant's own photo (false: product photo fallback).
   * Read from the backend's `hasOwnImage`: outside an exchange collection it
   * copies the product photo into `variant.image`, so the photo alone can't
   * tell. Older payloads without the flag fall back to comparing it with the
   * product photo (ignoring `?v=`), which misreads a variant whose own photo
   * is also the product's main photo.
   */
  isVariantImage: boolean;
  /** Option values (e.g. "M / Blue"), null for a "Default Title" variant. */
  label: string | null;
  sku: string | null;
};

/** Display label of an option value; the raw value is what matches. */
type TranslateOptionValue = (
  productId: string | number | null | undefined,
  optionName: string,
  value: string,
) => string;

/**
 * Resolves what the customer gets back for an exchange from the order's
 * `variantByLineItemId` collection: product title, image, option label and SKU
 * of `exchangeVariantId`. Returns null when the variant can't be found (it left
 * the collection since, out of stock or price gap).
 */
export function getExchangeVariantDisplay(
  order: Order | null,
  lineItemId: string,
  exchangeVariantId: string,
  translateValue: TranslateOptionValue = (_productId, _name, value) => value,
): ExchangeVariantDisplay | null {
  const collection = (order as unknown as Record<string, unknown>)
    ?.variantByLineItemId as
    | Record<string, { products: Product[] }>
    | undefined;
  const products = collection?.[lineItemId]?.products ?? [];
  for (const product of products) {
    const variant = product.variants.find((v) => v.id === exchangeVariantId);
    if (variant) {
      return {
        productId: product.productId ? String(product.productId) : null,
        productTitle: product.productTitle || null,
        image: variant.image || product.image || null,
        isVariantImage:
          variant.hasOwnImage ??
          (!!variant.image &&
            (!product.image ||
              stripImageQuery(variant.image) !==
                stripImageQuery(product.image))),
        label:
          getVariantOptionsLabel(
            variant.selectedOptions.map((o) =>
              translateValue(product.productId, o.name, o.value),
            ),
            " / ",
          ) || null,
        sku: variant.sku || null,
      };
    }
  }
  return null;
}
